import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

export default function LoginPage() {
  const { user, loading, login } = useAuth();
  const [loggingIn, setLoggingIn] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      toast.success("Selamat datang kembali, Admin!");
    }
  }, [user, loading]);

  if (loading)
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0a0908]">
        <div className="animate-spin w-8 h-8 border-2 border-[#d4af37] border-t-transparent rounded-full" />
      </div>
    );

  if (user) return <Navigate to="/admin" replace />;

  const handleGoogleLogin = async () => {
    setLoggingIn(true);
    try {
      await login();
      toast.success("Login berhasil!");
    } catch (err) {
      const message =
        err?.code === "auth/popup-closed-by-user"
          ? "Popup login ditutup. Silakan coba lagi."
          : err?.code === "auth/popup-blocked"
            ? "Popup diblokir browser. Izinkan popup untuk situs ini."
            : "Gagal login. Silakan coba lagi.";
      toast.error(message);
    } finally {
      setLoggingIn(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0908] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Subtle grain overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='g'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23g)' opacity='0.04'/%3E%3C/svg%3E\")",
        }}
      />

      {/* Decorative gold orbs */}
      <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-[#d4af37]/5 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-[#d4af37]/5 blur-3xl" />

      <div className="relative w-full max-w-md">
        {/* Card */}
        <div className="bg-[#15120f] border border-[#d4af37]/15 rounded-lg p-8 sm:p-10 shadow-2xl shadow-black/50">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/25 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-[#d4af37]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                />
              </svg>
            </div>
          </div>

          {/* Heading */}
          <h1 className="text-center text-2xl font-display text-[#fdfbf7] mb-1">
            Admin Panel
          </h1>
          <p className="text-center text-sm text-[#a6a196] mb-8">
            Sanggar Rias Adibah
          </p>

          {/* Google Button */}
          <button
            onClick={handleGoogleLogin}
            disabled={loggingIn}
            className="w-full flex items-center justify-center gap-3 px-6 py-3 rounded-lg border border-[#d4af37]/25 bg-[#0a0908] hover:bg-[#1a1816] text-[#fdfbf7] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
          >
            {loggingIn ? (
              <div className="animate-spin w-5 h-5 border-2 border-[#d4af37] border-t-transparent rounded-full" />
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
            )}
            <span className="text-sm font-medium">
              {loggingIn ? "Memasuki..." : "Masuk dengan Google"}
            </span>
          </button>

          {/* Footer */}
          <p className="text-center text-xs text-[#a6a196] mt-6">
            Akses terbatas untuk admin Sanggar Rias Adibah
          </p>
        </div>
      </div>
    </div>
  );
}
